import { defineStore } from 'pinia';
import { ref } from 'vue';
import axiosInstance from '@/shared/utils/axios';
import { Branch } from '@/types/types';
import { useAuthStore } from '@/core/auth/auth';

export interface BranchesData {
  branches: Branch[];
  totalCount: number;
  page: number;
  limit: number;
}

export const useBranchStore = defineStore('branch', () => {
  const branchesData = ref<BranchesData>({
    branches: [],
    totalCount: 0,
    page: 1,
    limit: 10
  });

  const fetchBranches = async (params = {}) => {
    try {
      const authStore = useAuthStore();
      const response = await axiosInstance.get('/api/branches', {
        params: {
          ...params,
          _t: Date.now() // Cache buster
        },
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        }
      });

      branchesData.value = {
        branches: response.data?.branches || [],
        totalCount: response.data?.pagination?.totalCount ?? 0,
        page: response.data?.pagination?.page ?? 1,
        limit: response.data?.pagination?.limit ?? 10
      };

      return response.data;
    } catch (error) {
      console.error('Error fetching branches:', error);
      throw error;
    }
  };

  const addBranch = async (branchData: Partial<Branch>) => {
    try {
      const response = await axiosInstance.post('/api/branches', branchData);
      const { branch: newBranch, branches, pagination } = response.data;

      branchesData.value = {
        branches,
        totalCount: pagination.totalCount,
        page: pagination.page,
        limit: pagination.limit
      };

      return newBranch;
    } catch (error) {
      console.error('Error adding branch:', error);
      throw error;
    }
  };

  const updateBranch = async (branchData: Branch) => {
    try {
      if (!branchData || !branchData._id) {
        throw new Error('Invalid branch data: Missing _id');
      }

      const response = await axiosInstance.put(`/api/branches/${branchData._id}`, branchData);
      const { branch: updatedBranch, branches, pagination } = response.data;

      if (!updatedBranch || !updatedBranch._id) {
        throw new Error('Invalid response: Updated branch data is missing or incomplete');
      }

      branchesData.value = {
        branches,
        totalCount: pagination.totalCount,
        page: pagination.page,
        limit: pagination.limit
      };

      return updatedBranch;
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error stack:', error.stack);
      }
      throw error;
    }
  };

  const getBranchById = async (id: string) => {
    try {
      const response = await axiosInstance.get(`/api/branches/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching branch by ID:', error);
      throw error;
    }
  };

  const deleteBranch = async (id: string) => {
    try {
      await axiosInstance.delete(`/api/branches/${id}`);
      branchesData.value.branches = branchesData.value.branches.filter(branch => branch._id !== id);
      branchesData.value.totalCount--;
    } catch (error) {
      console.error('Error deleting branch:', error);
      throw error;
    }
  };

  return {
    branchesData,
    fetchBranches,
    addBranch,
    updateBranch,
    getBranchById,
    deleteBranch
  };
});
